import {Injectable} from '@angular/core';
import {ToastController} from '@ionic/angular';
import {AuthService} from 'src/app/shared/services/auth.service';
import {SharedService} from 'src/app/shared/services/shared.service';
import {isEmpty, isNil, orderBy, remove} from 'lodash';
import {JobParams} from 'src/app/shared/modals/job-follow-up/models/jobs';
import {BehaviorSubject, interval, Observable, Subscription} from 'rxjs';
import {JobProgressNotification} from 'src/app/shared/modals/job-follow-up/models/job-progress-notification';
import {JobStatus} from 'src/app/shared/modals/job-follow-up/enums/job-status.enum';
import {distinctUntilChanged, switchMap, filter} from 'rxjs/operators';
import {BackendResponse} from 'auth/src/lib/services';
import {LocalStorageService} from 'src/app/shared/services/local-storage.service';
import {environment} from '../../../../environments/environment';
import {BaseClass} from '../../classes/base';
import {TranslationService} from '../../services/translation.service';

@Injectable({providedIn: 'root'})
export class JobFollowUpService extends BaseClass {
    private _pollingSubscriptions: { [key: string]: Subscription } = {};

    private get jobs() {
        return this.jobs$.value
    };

    public jobs$ = new BehaviorSubject<JobParams[]>([]);

    constructor(private authService: AuthService,
                private _sharedService: SharedService,
                protected _toastController: ToastController,
                protected _translationService: TranslationService,) {

        super(_toastController, _translationService);

        let jobs = this._getJobParams();
        jobs = jobs.filter(job => job && job.filters && job.filters['loggedUserId'] === this.authService.getLoggedUserId());
        this.jobs$.next(orderBy(jobs, ['jobId'], ['desc']));
        this.jobs$.pipe(distinctUntilChanged(), filter(jobParams => jobParams.length > 0)).subscribe((incomingJobs) => {
            localStorage.setItem(LocalStorageService.key.scheduleJobs, JSON.stringify(incomingJobs));
        });

    }


    public startPollingForJobProgress(service: () => Observable<BackendResponse<JobProgressNotification>>,
                                      job: Partial<JobParams>, currentRetry = 0, pollingInterval = 10000) {

        if (isNil(job) || isEmpty(job) || isNil(job.jobId) || isEmpty(job.jobId)) return;
        const maxRetries = 3; // Número máximo de tentativas
        if (!job.progress && job.jobId) {
            this._addJob(job as JobParams);
        }

        this._pollingSubscriptions[job.jobId] = interval(pollingInterval).pipe(
            switchMap(() => service())
        ).subscribe((response) => {
            const jobNotification = response.data;
            const jobId = response.data.jobId
            const jobParam = this._getJobById(jobId);
            if (jobNotification && jobParam) {
                jobParam.progress = jobNotification
                this._updateJob(jobParam);
                if (this._checkResponse(response, JobStatus.Completed)) {
                    if (jobNotification.data && !jobParam.downloaded) {
                        this._handleSuccessfulJob(jobId, jobNotification, jobParam);
                    } else if(this._checkResponse(response, JobStatus.Fail)) {
                        this._updateJob(jobParam);
                        this._unsubscribePollingSubscription(jobId);
                    } else {
                        this._unsubscribePollingSubscription(jobId);
                    }
                }
            }
        }, error => {
            currentRetry++;
            pollingInterval = pollingInterval * currentRetry; // Aumenta o intervalo em 10 segundos a cada erro
            if (currentRetry < maxRetries) {
                const jobParam = this._getJobById(job.jobId);
                jobParam ? this.startPollingForJobProgress(service, jobParam, currentRetry, pollingInterval)
                    : this.startPollingForJobProgress(service, job, currentRetry, pollingInterval);
            } else {
                this._pollingSubscriptions[job.jobId].unsubscribe();
                throw new Error('generic.jobs.maxRetriesExceeded');
            }
        });
    }

    private _handleSuccessfulJob(jobId: string, jobNotification: any, jobParam: any): void {
        window.open(jobNotification.data, '_blank');
        jobParam.downloaded = true;
        this._updateJob(jobParam);
        this._unsubscribePollingSubscription(jobId);
    }

    private _unsubscribePollingSubscription(jobId: string): void {
        const subscription = this._pollingSubscriptions[jobId];

        if (subscription) {
            subscription.unsubscribe();
            delete this._pollingSubscriptions[jobId];
            this.notify(this._translationService.getTranslation('Report.SendByEmail'), 'success');
        }
    }

    private _addJob(incomingJob: JobParams) {
        const currentJob = incomingJob.jobId && this.jobs.find(job => job.jobId === incomingJob.jobId);
        if (!currentJob && incomingJob.filters) {
            incomingJob.filters['loggedUserId'] = this.authService.getLoggedUserId();
        }
        if (!currentJob && !incomingJob.progress) {
            incomingJob.progress = new JobProgressNotification<string>({
                status: JobStatus.Enqueued,
                message: 'generic.Job.Enqueued',
                progressPercentage: 0,
            })
        }
        const jobs = isNil(currentJob) ? [...this.jobs, incomingJob] : [...this.jobs];
        this.jobs$.next(orderBy(jobs, ['jobId'], ['desc']));
    }

    private _getJobById(jobId: string): JobParams {
        return this.jobs.find(job => job.jobId === jobId);
    }

    private _updateJob(incomingJob: JobParams) {
        const jobs = [...this.jobs];
        const index = jobs.findIndex(job => job.jobId === incomingJob.jobId);
        if (index < 0) {
            throw new Error('generic.job.not.found');
        }
        remove(jobs, (job) => job.jobId === incomingJob.jobId);
        if (incomingJob.filters) {
            incomingJob.filters['loggedUserId'] = this.authService.getLoggedUserId();
        }
        this.jobs$.next(orderBy([...jobs, incomingJob], ['jobId'], ['desc']));
    }

    private _getJobParams() {
        const jsonResponse = localStorage.getItem(LocalStorageService.key.scheduleJobs);
        if (isNil(jsonResponse) || isEmpty(jsonResponse)) return [] as JobParams[];
        return JSON.parse(jsonResponse) as JobParams[];
    }

    private _checkResponse(response: BackendResponse<JobProgressNotification>, jobStatus: JobStatus): boolean {
        return (
            response &&
            response.success &&
            response.data.status === jobStatus
        );
    }
}
